// Where you are in signing in, drawn as four stages above the form.
//
// WHY THIS COMPONENT IS A SECURITY SURFACE, WHICH IS NOT OBVIOUS FROM LOOKING AT
// IT. SignIn.tsx's header and api/signIn.ts's header both set out at length that
// the login path is built around not revealing whether an address has an account,
// and not revealing whether an account has an authenticator, until the emailed
// code has been proved right. The server answers identically either way and the
// first request's body is the same shape for every account in existence.
//
// A PROGRESS INDICATOR IS THE EASIEST WAY IN THE CONSOLE TO THROW THAT AWAY, and
// the way it happens is the way that looks like good design: draw the stages the
// person will actually need, so an account with an authenticator gets three
// stages and an account without gets two. Then the number of stages drawn on the
// very first screen — before anybody has typed a code, before anything has been
// proved — is a public answer to "which of these addresses has two-factor turned
// on". A stranger types an address, counts what appears, and knows which mailbox
// is worth stealing. That is the same question login.ts refuses to answer on a
// route, refused here by a component instead.
//
// SO THE STAGES ARE ALWAYS THE SAME FOUR. Not four because four is tidy: four
// because a count that varies is the leak, and the only count that cannot vary is
// a constant one. api/signIn.ts holds the list as a module constant and computes
// the states from the step alone — it takes no argument that could carry anything
// about the account. The sentence above the bar names the current stage; the
// four segments stay even when the authenticator will never be asked for.
// check.mjs proves the property from the other side, by driving an account with a
// factor and an account without through this form and comparing the markup this
// component renders before either has spent a code.
//
// A LIST OF STAGES RATHER THAN A PERCENTAGE BAR, taking the owner's "progress
// bar and step indicators" as the intent. The fourth stage is the console
// opening, which never sits as `now` on this surface, so a four-segment bar that
// filled its last segment here would be lying about where somebody is. Each item
// is its own segment of the bar (a border on the item) so there is no second
// representation of the same position to disagree with.
//
// WHAT IS CONVEYED WITHOUT COLOUR, because the accent is the only thing on this
// strip that distinguishes a stage by hue and somebody may not see it. Three
// other signals carry the same fact: the sentence above the list names the
// current stage in words and counts it, the current item is marked
// `aria-current="step"` for a screen reader, and every item states its own state
// in text that is positioned off-screen rather than removed. ReportNav.tsx makes
// the same argument for its tab strip in its own words, and this borrows the
// reasoning rather than restating the rule.

import type { ReactNode } from "react";
import { SIGN_IN_STAGES, signInStages, type SignInStage, type SignInStep } from "../api/signIn.js";

export type SignInStepsProps = {
  readonly step: SignInStep;
  /**
   * Both factors are proved and the design's step 4 is drawn ("Both factors
   * proved", console-gate.jsx). Set only after a session has been granted, so
   * it can say nothing about an account that was not already proved.
   */
  readonly opened?: boolean;
};

/** Step 4 of 4, the console opening: every stage before it done. */
const OPENED: readonly SignInStage[] = SIGN_IN_STAGES.map(({ id, title }, index) => ({
  id,
  title,
  state: index === SIGN_IN_STAGES.length - 1 ? "now" : "done",
}));

export function SignInSteps({ step, opened = false }: SignInStepsProps): ReactNode {
  const stages = opened ? OPENED : signInStages(step);
  const at = stages.findIndex((stage) => stage.state === "now");
  const here = stages[at];

  return (
    <nav className="console-steps gate-steps" aria-label="Signing in">
      {/* The count in words, and now the whole of what is said rather than a
          caption over a list — see the note on the titles below. `role="status"`
          rather than `alert`: moving from one stage to the next is a
          progression, and a screen reader should say it without it arriving as
          something having gone wrong. SignIn.tsx makes the same distinction
          about the refusal box, for the same reason. */}
      {here === undefined ? null : (
        <p className="gate-eyebrow console-steps-count" role="status">
          Step {at + 1} of {stages.length} · {here.title}
        </p>
      )}

      {/* THE TITLES ARE NO LONGER DRAWN, AND THE BAR IS THEREFORE DECORATION.
          This was a list of four named stages with each one's state written
          off-screen beside it. The design draws four rules and puts the words in
          the line above, which is what the screenshots show and what the panel
          below now matches.

          Hiding it from the accessibility tree is the right call ONLY because
          the titles went with it. An aria-hidden bar beside a visible list would
          be information available to one reader and not another; an aria-hidden
          bar under a sentence that already names the stage and counts it is a
          second rendering of a fact that has just been stated in full. What a
          screen reader gets here — "Step 2 of 4, Code" — is everything a sighted
          reader gets, which was not true of four segments on their own.

          WHAT DID NOT CHANGE IS THE COUNT. Four, always, for every address
          alive: signInStages computes from the step and takes no argument that
          could carry anything about the account, so a strip whose length
          answered "does this address have two-factor on" is not a state this
          can reach. That is the property check.mjs drives from both sides, and
          it is the reason this file has a header at all. */}
      <div className="gate-bar" aria-hidden="true">
        {stages.map((stage, index) => (
          <i key={stage.id} className={index <= at ? "on" : undefined} />
        ))}
      </div>
    </nav>
  );
}
